import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { API, useAuth } from "@/contexts/AuthContext";
import CreditsCard from "@/components/dashboard/CreditsCard";
import FounderRevenueBrief from "@/components/dashboard/FounderRevenueBrief";
import {
  Wallet, ReceiptEuro, KanbanSquare, Users, FilePlus, UserPlus, FolderPlus, Send,
  Sparkles, ArrowRight, ReceiptText, CheckCircle2, Globe, X, Download,
} from "lucide-react";

const KPIS = [
  { key: "monthly_revenue", label: "Monthly Revenue", icon: Wallet, prefix: "€" },
  { key: "open_invoices", label: "Open Invoices", icon: ReceiptEuro },
  { key: "active_projects", label: "Active Projects", icon: KanbanSquare },
  { key: "team_members", label: "Team Members", icon: Users },
];

const QUICK = [
  { label: "New Invoice", icon: FilePlus, to: "/dashboard/finance" },
  { label: "New Client", icon: UserPlus, to: "/dashboard/sales" },
  { label: "New Project", icon: FolderPlus, to: "/dashboard/projects" },
  { label: "Invite Team Member", icon: Send, to: "/dashboard/team" },
];

const ACTIVITY_ICON = {
  user_plus: UserPlus,
  receipt: ReceiptText,
  folder_plus: FolderPlus,
  sparkles: Sparkles,
};

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Math.max(0, Date.now() - then);
  const min = Math.floor(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}

function formatTrialRemaining(expiresAt, now) {
  const end = new Date(expiresAt).getTime();
  if (!Number.isFinite(end)) return null;
  const minutes = Math.max(0, Math.ceil((end - now) / 60000));
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;
  return `${days} dagen ${hours} uur ${mins} min`;
}

export default function Dashboard() {
  const { user } = useAuth();
  const workspaceKey = user?.active_workspace || user?.id;
  const [trialClockNow, setTrialClockNow] = useState(() => Date.now());
  const ownWorkspace = !user?.active_workspace || user.active_workspace === user?.id;
  const trialUser = user?.workspace_context || user;
  const trialExpiry = trialUser?.is_trial ? trialUser?.trial_expires_at : null;
  const trialRemaining = trialExpiry ? formatTrialRemaining(trialExpiry, trialClockNow) : null;

  useEffect(() => {
    if (!trialExpiry) return undefined;
    const timer = window.setInterval(() => setTrialClockNow(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, [trialExpiry]);
  const [summaryRecord, setData] = useState(null);
  const data = summaryRecord?.workspaceKey === workspaceKey ? summaryRecord.result : null;
  const [summaryError, setSummaryError] = useState("");
  const [exportBusy, setExportBusy] = useState(false);
  const [exportError, setExportError] = useState("");
  const exportPending = useRef(false);
  const activeWorkspaceRef = useRef(workspaceKey);
  activeWorkspaceRef.current = workspaceKey;
  const downloadQbr = async () => {
    if (exportPending.current) return;
    exportPending.current = true;
    setExportBusy(true);
    setExportError("");
    const requestedWorkspace = workspaceKey;
    try {
      const response = await axios.get(`${API}/finance/qbr.pptx`, { responseType: "blob" });
      if (activeWorkspaceRef.current !== requestedWorkspace) return;
      const blob = response.data;
      const type = (response.headers["content-type"] || "").split(";")[0].trim().toLowerCase();
      if (!(blob instanceof Blob) || !blob.size ||
          type !== "application/vnd.openxmlformats-officedocument.presentationml.presentation") {
        throw new Error("Unexpected export response");
      }
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      try {
        link.href = url;
        link.download = "zynthoro-business-review.pptx";
        document.body.appendChild(link);
        link.click();
      } finally {
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 60000);
      }
    } catch {
      if (activeWorkspaceRef.current === requestedWorkspace) {
        setExportError("The PowerPoint could not be downloaded. Check your access and try again.");
      }
    } finally {
      exportPending.current = false;
      setExportBusy(false);
    }
  };
  const [summaryLoading, setSummaryLoading] = useState(true);
  const summarySequence = useRef(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const [purchaseBanner, setPurchaseBanner] = useState(searchParams.get("purchase") === "success");

  const dismissPurchaseBanner = () => {
    setPurchaseBanner(false);
    if (searchParams.get("purchase")) {
      const next = new URLSearchParams(searchParams);
      next.delete("purchase");
      setSearchParams(next, { replace: true });
    }
  };

  const loadSummary = useCallback(async () => {
    const sequence = ++summarySequence.current;
    setSummaryLoading(true);
    try {
      const { data: result } = await axios.get(`${API}/dashboard/summary`);
      if (sequence !== summarySequence.current) return;
      if (!result?.kpis || !Array.isArray(result.recent_activity)) {
        throw new Error("Invalid dashboard response.");
      }
      setData({ workspaceKey, result });
      setSummaryError("");
    } catch {
      if (sequence !== summarySequence.current) return;
      setSummaryError("Dashboard information is temporarily unavailable. Please try again.");
    } finally {
      if (sequence === summarySequence.current) setSummaryLoading(false);
    }
  }, [workspaceKey]);

  useEffect(() => {
    setData(null);
    setSummaryError("");
    loadSummary();
    return () => { summarySequence.current += 1; };
  }, [loadSummary]);

  return (
    <div className="space-y-8" data-testid="dashboard">
      {trialUser?.is_trial && trialRemaining !== null && (
        <section
          data-testid="dashboard-trial-countdown"
          className="rounded-xl border border-[#E4D4A0] bg-[#FFFBED] p-5"
          aria-live="off"
        >
          <p className="text-[13px] font-semibold text-[#73571A]">Resterende proefperiode</p>
          <p className="mt-1 text-[22px] font-bold text-[#0A1628]" data-testid="trial-time-remaining">
            {trialRemaining}
          </p>
          <p className="mt-1 text-[12px] text-[#73571A]">
            Je gekozen abonnement wordt na afloop actief, tenzij je de trial vóór het eindmoment opzegt.
          </p>
        </section>
      )}
      {ownWorkspace && purchaseBanner && (
        <div
          data-testid="dashboard-purchase-banner"
          className="rounded-xl border p-5 flex items-start gap-4"
          style={{ borderColor: "rgba(16,185,129,0.35)", background: "rgba(16,185,129,0.08)" }}
        >
          <span
            className="shrink-0 inline-flex items-center justify-center rounded-full"
            style={{ background: "#059669", width: 40, height: 40 }}
          >
            <CheckCircle2 size={20} className="text-white" />
          </span>
          <div className="flex-1">
            <p className="text-[15px] font-semibold text-[#0A1628]">Back from checkout</p>
            <p className="text-[13px] text-[#333] mt-1">
              Your plan and access update after payment confirmation. Check Settings for your current plan and Domains for the status of any domain registration.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Link
                to="/dashboard/website-builder"
                data-testid="dashboard-purchase-cta-site"
                className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[13px] font-semibold text-white"
                style={{ background: "#202B60" }}
              >
                <Sparkles size={13} /> Start building your site
              </Link>
              <Link
                to="/dashboard/domains"
                data-testid="dashboard-purchase-cta-domain"
                className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[13px] font-semibold border border-[#202B60] text-[#202B60] bg-white"
              >
                <Globe size={13} /> Manage domains
              </Link>
            </div>
          </div>
          <button
            type="button"
            onClick={dismissPurchaseBanner}
            data-testid="dashboard-purchase-banner-dismiss"
            className="p-1.5 rounded hover:bg-white/60 text-[#0A1628]"
            aria-label="Dismiss"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* AI Credits balance + top-up */}
      <CreditsCard />

      {/* Founder-only revenue snapshot */}
      <FounderRevenueBrief />

      {/* Phase 5B.7: Export quarterly business review as PowerPoint */}
      <div className="flex items-center justify-end">
        <button
          onClick={downloadQbr}
          disabled={exportBusy}
          aria-busy={exportBusy}
          data-testid="dashboard-export-qbr-pptx-btn"
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-[13px] font-semibold text-white"
          style={{ background: "#202B60" }}
          title="Export Quarterly Business Review as PowerPoint (.pptx)"
        >
          <Download size={14} /> {exportBusy ? "Preparing PowerPoint…" : "Export PowerPoint"}
        </button>
      </div>

      {exportError && <p role="alert" className="text-sm text-amber-800">{exportError}</p>}

      {summaryError && (
        <div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-slate-700">
          <p>{summaryError}{data ? " Previously loaded figures remain visible." : ""}</p>
          <button type="button" disabled={summaryLoading} onClick={loadSummary}
            className="mt-2 rounded border px-3 py-1.5 disabled:opacity-50">
            {summaryLoading ? "Loading…" : "Retry dashboard"}
          </button>
        </div>
      )}

      {/* KPI cards */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {KPIS.map((k) => (
          <div key={k.key} className="bg-white border border-[#eee] rounded-xl p-5" data-testid={`kpi-${k.key}`}>
            <div className="flex items-center justify-between">
              <span className="zy-domain-icon" style={{ width: 36, height: 36, marginBottom: 0 }}>
                <k.icon size={16} />
              </span>
              <span className="text-[11px] uppercase tracking-wider text-[#999] font-semibold">{k.label}</span>
            </div>
            <p className="mt-4 text-[26px] font-bold tracking-tight text-black">
              {typeof data?.kpis?.[k.key] === "number"
                ? `${k.prefix || ""}${data.kpis[k.key].toLocaleString("en-US",
                    k.key === "monthly_revenue" ? { minimumFractionDigits: 2, maximumFractionDigits: 2 } : {})}`
                : "—"}
            </p>
          </div>
        ))}
      </section>

      {/* Quick actions */}
      <section>
        <h2 className="text-[15px] font-semibold mb-3">Quick actions</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {QUICK.map((q) => (
            <Link
              key={q.label}
              to={q.to}
              className="bg-white border border-[#eee] rounded-xl p-4 hover:border-[#202B60] transition-all"
              data-testid={`quick-${q.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <span className="zy-domain-icon" style={{ width: 36, height: 36, marginBottom: 12 }}>
                <q.icon size={16} />
              </span>
              <p className="text-[13.5px] font-semibold">{q.label}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* AI Suggestions */}
      <section className="bg-white border border-[#eee] rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles size={16} style={{ color: "#202B60" }} />
          <h2 className="text-[14.5px] font-semibold">Zynthoro Assist suggests:</h2>
        </div>
        <ul className="space-y-2.5">
          {(data?.ai_suggestions || []).map((s, i) => (
            <li key={i} className="flex items-start gap-2 text-[13.5px] text-[#333]">
              <ArrowRight size={14} className="mt-1 shrink-0" style={{ color: "#202B60" }} />
              <span>{s}</span>
            </li>
          ))}
          {!data?.ai_suggestions?.length && (
            <li className="text-[13px] text-[#999]">{summaryLoading ? "Loading suggestions…" : summaryError ? "Suggestions unavailable." : "No suggestions yet."}</li>
          )}
        </ul>
      </section>

      {/* Recent activity */}
      <section
        className="bg-white border border-[#eee] rounded-xl p-6"
        data-testid="dashboard-recent-activity"
      >
        <h2 className="text-[14.5px] font-semibold mb-3">Recent activity</h2>
        {(data?.recent_activity || []).length === 0 ? (
          <p className="text-[13.5px] text-[#888]">
            {summaryLoading ? "Loading activity…" : summaryError && !data ? "Activity unavailable." : "Your activity will appear here. Start by creating your first invoice or project."}
          </p>
        ) : (
          <ul className="divide-y divide-[#f2f2f2]">
            {data.recent_activity.map((a, i) => {
              const IconEl = ACTIVITY_ICON[a.icon] || Sparkles;
              const inner = (
                <>
                  <span
                    className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center"
                    style={{ background: "#EAF0FF", color: "#202B60" }}
                  >
                    <IconEl size={15} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13.5px] font-medium text-black truncate">{a.title}</p>
                    <p className="text-[12px] text-[#888] mt-0.5 truncate">{a.subtitle}</p>
                  </div>
                  <span className="text-[11.5px] text-[#999] shrink-0 tabular-nums">
                    {timeAgo(a.timestamp)}
                  </span>
                </>
              );
              const commonCls = "flex items-center gap-3 py-3";
              return (
                <li key={i} data-testid={`activity-item-${i}`}>
                  {a.href ? (
                    <Link to={a.href} className={`${commonCls} hover:bg-[#fafafa] -mx-2 px-2 rounded-md transition-colors`}>
                      {inner}
                    </Link>
                  ) : (
                    <div className={commonCls}>{inner}</div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
